import React, { useCallback, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  DangerAlert,
  FormItem,
  FormRow,
  InfoAlert,
  Link,
  SecondaryBtn,
  SecondaryLink,
  SvgReload,
  Table,
  WaitingAlert,
} from '@tmapy/style-guide';

import type { EpsiDocAttachmentResponse } from '../../openapi/types';
import {
  epsiDocsRetrieveOperation,
  useEpsiDocsRetrieveResource,
} from '../../openapi/operations/epsiDocsRetrieve';

import { SSE_TYPE, useDownloadLink, useLogDownloadLink, useSSE } from '../../hooks';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';
import { ATTACHMENT_TYPES } from '../../docs/constants';

import { attachmentTypesMessages } from '../../docs/components/docsMessages';
import { DocDownloadFiles } from '../../docs/components/DocDownloadFiles';
import { FileIconWithBadge } from '../../docs/components/FileIconWithBadge';
import { EpsiBadge } from './EpsiBadge';
import { EpsiRetryAlert } from './EpsiRetryAlert';
import {
  isEpsiDownloadPhase,
  isEpsiRetryable,
  SSE_REFETCH_DELAY,
  SSE_REFETCH_MAX_WAIT,
} from './constants';
import { epsiMessages, epsiTypeMessage } from './epsiMessages';
import { useEpsiAcl } from './useEpsiAcl';
import { epsiRetryQueryState, useEpsiRetry } from './useEpsiRetry';

export type EpsiDetailProps = {
  docId?: number;
};

export const EpsiDetail: React.FC<EpsiDetailProps> = ({ docId }) => {
  const formatMessage = useMessage();
  const acl = useEpsiAcl();
  const [showDownloadError, setShowDownloadError] = useState(false);

  const { queryState, data, refetch } = useEpsiDocsRetrieveResource(docId ? { id: docId } : null);

  /**
   * Sada se přepíná mezi stavy na pozadí (stahování, import), detail se proto
   * drží aktuální přes SSE. Kanál nefiltruje podle sady, takže refetch přijde
   * i po cizí změně — zprávy chodí v dávkách, refetch se slučuje.
   */
  const refetchDebounced = useDebouncedCallback(refetch, SSE_REFETCH_DELAY, {
    maxWait: SSE_REFETCH_MAX_WAIT,
  });
  useSSE(SSE_TYPE.EPSI, docId, refetchDebounced);

  const { retry, retryStates, dismissRetry } = useEpsiRetry(refetch);
  const retryState = docId === undefined ? undefined : retryStates[docId];

  const handleRetryBtnClick = useCallback(() => {
    if (docId !== undefined) {
      retry(docId);
    }
  }, [retry, docId]);

  const handleRetryAlertHide = useCallback(() => {
    if (docId !== undefined) {
      dismissRetry(docId);
    }
  }, [dismissRetry, docId]);

  const docUrl = docId
    ? `${globalThis.DMX_CONFIG.server}${epsiDocsRetrieveOperation({ id: docId }).path}`
    : '';

  const downloadAllLink = useDownloadLink(docUrl, null, 'download_all_attachments');
  const importLogLink = useLogDownloadLink(
    docUrl,
    null,
    'download_import_log',
    setShowDownloadError,
  );

  const attachments = data?.attachments ?? [];
  const typeMessage = data?.type ? epsiTypeMessage(data.type) : undefined;
  /** Ve fázi stahování soubory ještě neexistují — prázdný seznam není chyba. */
  const isDownloadPhase = isEpsiDownloadPhase(data?.state);
  const canRetry =
    acl.epsiActionRetry && docId !== undefined && isEpsiRetryable(data?.state);

  return (
    <MainContent>
      <article className='sg-u-vs-4'>
        <div className='dmx-doc--head sg-u-vs-2'>
          <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
            <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
              <PageTitle>
                <FormattedMessage {...epsiMessages.detailTitle} values={{ id: docId }} />
              </PageTitle>
              {data?.state && <EpsiBadge state={data.state} />}
            </header>
            {canRetry && (
              <BtnBar>
                <SecondaryBtn
                  icon={{ element: <SvgReload /> }}
                  queryState={epsiRetryQueryState(retryState)}
                  onClick={handleRetryBtnClick}
                >
                  <FormattedMessage {...epsiMessages.retryBtn} />
                </SecondaryBtn>
              </BtnBar>
            )}
          </div>

          {retryState && retryState.phase !== 'waiting' && (
            <EpsiRetryAlert state={retryState} onHide={handleRetryAlertHide} />
          )}
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...epsiMessages.detailStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...epsiMessages.detailStateFail} />
            </DangerAlert>
          )}
          {showDownloadError && (
            <DangerAlert>
              <FormattedMessage {...epsiMessages.downloadFail} />
            </DangerAlert>
          )}
        </div>

        {data && (
          <div className='sg-u-vs-4'>
            <section className='sg-u-box sg-a-p-2 sg-u-vs-1'>
              <FormRow>
                <FormItem label={formatMessage(epsiMessages.columnType)}>
                  {typeMessage ? <FormattedMessage {...typeMessage} /> : data.type}
                </FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(epsiMessages.columnPspiDatumOd)}>
                  {data.pspi_datum_od && (
                    <time dateTime={data.pspi_datum_od}>
                      <FormattedDate value={new Date(data.pspi_datum_od)} />
                    </time>
                  )}
                </FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(epsiMessages.columnCreated)}>
                  {data.created && (
                    <time dateTime={data.created}>
                      <FormattedDate value={new Date(data.created)} />{' '}
                      <FormattedTime
                        value={new Date(data.created)}
                        hour='numeric'
                        minute='numeric'
                        second='numeric'
                      />
                    </time>
                  )}
                </FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(epsiMessages.columnImported)}>
                  {data.imported && (
                    <time dateTime={data.imported}>
                      <FormattedDate value={new Date(data.imported)} />{' '}
                      <FormattedTime
                        value={new Date(data.imported)}
                        hour='numeric'
                        minute='numeric'
                        second='numeric'
                      />
                    </time>
                  )}
                </FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(epsiMessages.columnSource)}>{data.source}</FormItem>
              </FormRow>
            </section>

            <section>
              <h3 className='sg-u-type-h2 sg-a-ml-2'>
                <FormattedMessage {...epsiMessages.filesTitle} />
              </h3>
              <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2'>
                    <FormattedMessage {...epsiMessages.filesDocs} />
                  </h3>
                  {attachments.length < 1 ? (
                    <div className='sg-a-mt-1'>
                      <InfoAlert>
                        <FormattedMessage
                          {...(isDownloadPhase
                            ? epsiMessages.attachmentsDownloading
                            : epsiMessages.attachmentsEmpty)}
                        />
                      </InfoAlert>
                    </div>
                  ) : (
                    <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-a-mt-1 sg-u-vs-2'>
                      <div className='sg-a-mx--2'>
                        <Table>
                          <thead>
                            <tr>
                              <th />
                              <th>
                                <FormattedMessage {...epsiMessages.attachmentName} />
                              </th>
                              <th>
                                <FormattedMessage {...epsiMessages.attachmentType} />
                              </th>
                              <th className='sg-a-ta-r'>
                                <FormattedMessage {...epsiMessages.attachmentCreated} />
                              </th>
                              <th className='sg-a-ta-r'>
                                <FormattedMessage {...epsiMessages.attachmentSize} />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {attachments.map((attachment) => (
                              <EpsiAttachmentItem
                                key={attachment.id}
                                docUrl={docUrl}
                                attachment={attachment}
                              />
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <BtnBar>
                        <SecondaryLink {...downloadAllLink} download>
                          <FormattedMessage {...epsiMessages.downloadAllBtn} />
                        </SecondaryLink>
                      </BtnBar>
                    </div>
                  )}
                </section>

                <section className='sg-a-mt-2'>
                  <DocDownloadFiles
                    title={<FormattedMessage {...epsiMessages.filesSystem} />}
                    docId={docId}
                    items={[
                      {
                        permission: data.has_import_log,
                        link: importLogLink,
                        label: <FormattedMessage {...epsiMessages.downloadImportLog} />,
                        ext: 'zip',
                      },
                    ]}
                  />
                </section>
              </div>
            </section>
          </div>
        )}
      </article>
    </MainContent>
  );
};

type EpsiAttachmentItemProps = {
  docUrl: string;
  attachment: EpsiDocAttachmentResponse;
};

const EpsiAttachmentItem: React.FC<EpsiAttachmentItemProps> = ({ docUrl, attachment }) => {
  const formatMessage = useMessage();
  const downloadLink = useDownloadLink(
    `${docUrl}attachments/${attachment.id}/`,
    null,
    'download_attachment',
  );

  return (
    <tr>
      <td className='sg-a-w-0'>
        <FileIconWithBadge name={attachment.name} />
      </td>
      <td>
        <Link {...downloadLink} severity='colorful' download={attachment.name}>
          <div className='sg-a-ow-a'>{attachment.name}</div>
        </Link>
      </td>
      <td>
        {formatMessage.lookup(
          attachmentTypesMessages,
          ATTACHMENT_TYPES[attachment.attachment_type],
        )}
      </td>
      <td className='sg-a-ta-r'>
        {attachment.created && (
          <time dateTime={attachment.created}>
            <FormattedDate value={new Date(attachment.created)} />{' '}
            <FormattedTime value={new Date(attachment.created)} />
          </time>
        )}
      </td>
      <td className='sg-a-ta-r'>{attachment.size}</td>
    </tr>
  );
};
